import { bunja } from 'bunja';
import { atom } from 'jotai';

import type { V2Position } from '@flipster/socket/r230522/position';

import { oneWayPositionLeverageBunja, perpAccountBunja } from './base';
import { formBunja } from './form';

/**
 * 오더폼 레버리지 chip 클릭 시 열 Adjust Leverage 모달의 형태.
 * needsOffHoursGuard: one-way 모드면 true (off-hours 시 조정 전 안내 대상). 실제 off-hours 여부는
 * guardOffHoursAlert가 판정하므로, 여기선 "이 액션이 안내 대상인지"만 나타낸다.
 */
export type AdjustLeverageIntent = { needsOffHoursGuard: boolean } & (
  | { kind: 'position'; position: V2Position }
  | { kind: 'form'; defaultLeverage: number }
);

/**
 * 오더폼 상태로부터 어떤 Adjust Leverage 모달을 열지 결정하는 순수 로직.
 * - one-way + 실포지션 → position 모달
 * - one-way + pending만 존재 → 그 pending leverage로 form 모달
 * - 그 외(one-way 미포지션 / hedge) → 현재 form leverage로 form 모달
 * one-way면 needsOffHoursGuard=true.
 */
export const resolveAdjustLeverageIntent = (params: {
  isOneWay: boolean;
  position: V2Position | undefined | null;
  pendingLeverage: number | undefined;
  formLeverage: number;
}): AdjustLeverageIntent => {
  const { isOneWay, position, pendingLeverage, formLeverage } = params;

  if (isOneWay) {
    if (position) return { kind: 'position', needsOffHoursGuard: true, position };
    if (pendingLeverage != null) return { kind: 'form', needsOffHoursGuard: true, defaultLeverage: pendingLeverage };
  }

  return { kind: 'form', needsOffHoursGuard: isOneWay, defaultLeverage: formLeverage };
};

/**
 * 위 순수 로직을 오더폼 atom들과 배선한다. UI(index.tsx)는 이 atom을 consume해 overlay.open만 담당한다.
 */
export const adjustLeverageIntentBunja = bunja(() => {
  const { isOneWayAtom } = bunja.use(perpAccountBunja);
  const { currentSymbolPositionAtom, currentSymbolPendingPositionsAtom } = bunja.use(oneWayPositionLeverageBunja);
  const { orderStateAtom } = bunja.use(formBunja);

  const adjustLeverageIntentAtom = atom<AdjustLeverageIntent>((get) =>
    resolveAdjustLeverageIntent({
      isOneWay: get(isOneWayAtom),
      position: get(currentSymbolPositionAtom),
      pendingLeverage: get(currentSymbolPendingPositionsAtom)[0]?.initLeverage,
      formLeverage: get(orderStateAtom).leverage,
    }),
  );

  return { adjustLeverageIntentAtom };
});
adjustLeverageIntentBunja.debugLabel = 'adjustLeverageIntentBunja';
